import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type AgreementSubject, type IndividualAgreement } from '../api';
import { date, money, type Dictionary, type Locale } from '../i18n';
import * as Icon from '@firmeto/design/Icons';

/**
 * Umowy indywidualne firmy albo biura (decyzja 45) — na kartach obu.
 *
 * Ponad cennik ustala się z klientem wyższy limit dokumentów przez API, sufit pakietu
 * indywidualnego i opłaty za nie; tu operator je wpisuje, a rozliczenie miesięczne je liczy.
 * Formularz nie pozwala edytować obowiązującej umowy — warunki zmienia nowa umowa, żeby korekta
 * za miniony miesiąc liczyła się na warunkach, które wtedy obowiązywały.
 */

function thisMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

/** Ostatni dzień miesiąca RRRR-MM — umowa kończy się z końcem miesiąca, tak jak rozliczenie. */
function monthEnd(month: string): string {
  const [year, number] = month.split('-').map(Number);
  const last = new Date(Date.UTC(year, number, 0)).getUTCDate();
  return `${month}-${String(last).padStart(2, '0')}`;
}

const empty = { from: '', fee: '', limit: '', ceiling: '', reference: '' };

export function IndividualAgreements({ subject, t, locale }: { subject: AgreementSubject; t: Dictionary; locale: Locale }) {
  const [items, setItems] = useState<IndividualAgreement[] | null>(null);
  const [form, setForm] = useState({ ...empty, from: thisMonth() });
  const [ending, setEnding] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const forCompany = subject.kind === 'companies';

  const load = useCallback(async () => {
    try {
      setItems(await api.individualAgreements(subject));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
    // Zależy od pól, nie od obiektu — karta tworzy go przy każdym renderze
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject.kind, subject.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(action: () => Promise<string>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await action());
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  function save(event: React.FormEvent) {
    event.preventDefault();
    void act(async () => {
      await api.createIndividualAgreement(subject, {
        validFrom: `${form.from}-01`,
        validTo: null,
        monthlyFeeNet: form.fee.replace(',', '.').trim() || '0',
        apiDocumentLimit: form.limit.trim() ? Number(form.limit.replace(/\s/g, '')) : null,
        ceilingGross: !forCompany && form.ceiling.trim() ? form.ceiling.replace(',', '.').trim() : null,
        reference: form.reference.trim(),
      });
      setForm({ ...empty, from: thisMonth() });
      return t.agreements.saved;
    });
  }

  function end(agreement: IndividualAgreement, notStarted: boolean) {
    const month = notStarted ? agreement.validFrom.slice(0, 7) : ending[agreement.id] ?? thisMonth();
    void act(async () => {
      const result = await api.endIndividualAgreement(agreement.id, monthEnd(month));
      return result ? t.agreements.ended : t.agreements.cancelled;
    });
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="panel">
      <div className="hd">
        <div>
          <h4>{t.agreements.title}</h4>
          <p>{forCompany ? t.agreements.leadCompany : t.agreements.leadOffice}</p>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {notice && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{notice}</b>
          </div>
        </div>
      )}

      {items === null ? (
        <p className="muted">{t.loading}</p>
      ) : items.length === 0 ? (
        <p className="muted">{t.agreements.none}</p>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.agreements.from}</th>
                <th>{t.agreements.to}</th>
                <th className="n">{t.agreements.fee}</th>
                <th className="n">{t.agreements.limit}</th>
                {!forCompany && <th className="n">{t.agreements.ceiling}</th>}
                <th className="wrap">{t.agreements.reference}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((agreement) => {
                const notStarted = agreement.validFrom > today;
                const over = agreement.validTo !== null && agreement.validTo < today;
                return (
                  <tr key={agreement.id}>
                    <td>{date(agreement.validFrom, locale)}</td>
                    <td>{agreement.validTo ? date(agreement.validTo, locale) : t.agreements.open}</td>
                    <td className="n mono">{money(agreement.monthlyFeeNet, locale)}</td>
                    <td className="n mono">
                      {agreement.apiDocumentLimit?.toLocaleString(locale) ?? (forCompany ? t.agreements.standard : t.agreements.noApi)}
                    </td>
                    {!forCompany && (
                      <td className="n mono">{agreement.ceilingGross ? money(agreement.ceilingGross, locale) : t.agreements.standard}</td>
                    )}
                    <td className="wrap">{agreement.reference}</td>
                    <td>
                      {over ? null : notStarted ? (
                        <button type="button" className="btn ghost sm" disabled={busy} onClick={() => end(agreement, true)}>
                          {t.agreements.cancel}
                        </button>
                      ) : (
                        <div className="row">
                          <input
                            className="inp mono"
                            type="month"
                            aria-label={t.agreements.endLabel}
                            style={{ maxWidth: 150 }}
                            min={thisMonth()}
                            value={ending[agreement.id] ?? thisMonth()}
                            onChange={(event) => setEnding({ ...ending, [agreement.id]: event.target.value })}
                          />
                          <button type="button" className="btn ghost sm" disabled={busy} onClick={() => end(agreement, false)}>
                            {t.agreements.end}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={save}>
        <div className="hd">
          <div>
            <h4>{t.agreements.add}</h4>
            <p>{t.agreements.addLead}</p>
          </div>
        </div>
        <div className="row">
          <label className="fld">
            <span>{t.agreements.from}</span>
            <input
              className="inp mono"
              type="month"
              style={{ maxWidth: 150 }}
              min={thisMonth()}
              value={form.from}
              onChange={(event) => setForm({ ...form, from: event.target.value })}
              required
            />
          </label>
          <label className="fld">
            <span>{t.agreements.fee}</span>
            <input
              className="inp mono"
              style={{ maxWidth: 140 }}
              inputMode="decimal"
              placeholder="0,00"
              value={form.fee}
              onChange={(event) => setForm({ ...form, fee: event.target.value })}
            />
          </label>
          <label className="fld">
            <span>{t.agreements.limit}</span>
            <input
              className="inp mono"
              style={{ maxWidth: 140 }}
              inputMode="numeric"
              placeholder="5000"
              value={form.limit}
              onChange={(event) => setForm({ ...form, limit: event.target.value })}
            />
          </label>
          {!forCompany && (
            <label className="fld">
              <span>{t.agreements.ceiling}</span>
              <input
                className="inp mono"
                style={{ maxWidth: 140 }}
                inputMode="decimal"
                value={form.ceiling}
                onChange={(event) => setForm({ ...form, ceiling: event.target.value })}
              />
            </label>
          )}
        </div>
        <label className="fld">
          <span>{t.agreements.reference}</span>
          <input
            className="inp"
            value={form.reference}
            onChange={(event) => setForm({ ...form, reference: event.target.value })}
            required
          />
        </label>
        <div className="row">
          <button type="submit" className="btn" disabled={busy || form.reference.trim() === ''}>
            {t.agreements.save}
          </button>
        </div>
      </form>
    </div>
  );
}
